import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Vehicle } from '@app-model/vehicle';
import { EvaluationVehicle } from '@app-model/evaluation';
import { OemDealerService, PotentialBuyer, PotentialBuyerFilters, PotentialBuyerModelFilter, PotentialBuyerRegion, PotentialBuyerResponse } from '@app-services/oem-dealer.service';
import { FastQuoteService } from '@app-services/fast-quote.service';
import { OpenService } from '@app-services/open.service';

export interface DialogPotentialDealersData {
  vehicle: Vehicle | EvaluationVehicle;
  fastQuoteId?: string;
}

@Component({
  selector: 'app-dialog-potential-dealers',
  templateUrl: './dialog-potential-dealers.component.html',
  styleUrls: ['./dialog-potential-dealers.component.scss']
})
export class DialogPotentialDealersComponent implements OnInit {

  filtersForm: FormGroup;
  potentialBuyers?: PotentialBuyerResponse;
  buyers: PotentialBuyer[] = [];
  isLoading = false;
  vehicle: Vehicle | EvaluationVehicle;
  private readonly fastQuoteId?: string;

  readonly regions: { label: string, value: PotentialBuyerRegion }[] = [
    { label: 'Cidade', value: 'CITY' },
    { label: 'Estado', value: 'UF' },
    { label: 'Região', value: 'REGION' },
    { label: 'Todos', value: 'COUNTRY' },
  ];

  readonly modelFilters: { label: string, value: PotentialBuyerModelFilter }[] = [
    { label: 'Versão e ano', value: 'TRIM_YEAR' },
    { label: 'Versão e anos similares', value: 'TRIM_SIMILAR' },
    { label: 'Modelo e ano', value: 'MODEL_YEAR' },
    { label: 'Modelo e anos similares', value: 'MODEL_SIMILAR' },
  ];

  readonly periodDaysOptions: number[] = [7, 15, 30, 60];

  readonly contextTags: { label: string, value: string, class: string }[] = [
    { label: 'Já comprou com você', value: 'BOUGHT_FROM_YOU', class: 'already-bought' },
    { label: 'Comprou modelo similar', value: 'BOUGHT_SIMILAR', class: 'similar-model' },
    { label: 'Disputou modelo similar', value: 'BID_RECENTLY', class: 'bid-recently' },
    { label: 'Compra desta marca', value: 'FREQUENT_MAKE_BUYER', class: 'already-bought-make' }
  ];

  constructor(
    public dialogRef: MatDialogRef<DialogPotentialDealersComponent>,
    @Inject(MAT_DIALOG_DATA) data: Vehicle | EvaluationVehicle | DialogPotentialDealersData,
    private readonly oemDealerService: OemDealerService,
    private readonly fastQuoteService: FastQuoteService,
    private readonly fb: FormBuilder,
    private readonly toastr: ToastrService,
    private readonly openService: OpenService,
  ) {
    if (this.isDialogData(data)) {
      this.vehicle = data.vehicle;
      this.fastQuoteId = data.fastQuoteId;
    } else {
      this.vehicle = data;
    }

    this.filtersForm = this.fb.group({
      region: ['UF'],
      modelFilter: ['MODEL_SIMILAR'],
      periodDays: [30],
      onlyBoughtFromSeller: [false],
    });
  }

  ngOnInit(): void {
    this.fetchPotentialBuyers();
    this.filtersForm.valueChanges.subscribe(() => this.fetchPotentialBuyers());
  }

  get fipePrice(): number {
    return this.potentialBuyers?.fipePrice ?? this.vehicle?.fipePrice;
  }

  get totalInterested(): number {
    return this.potentialBuyers?.totalInterested ?? 0;
  }

  get averageAffinityLabel(): string {
    return this.potentialBuyers?.averageAffinityLabel ?? '-';
  }

  get averageAffinityPercent(): number {
    return this.potentialBuyers?.averageAffinityPercent ?? 0;
  }

  onSearch(): void {
    this.fetchPotentialBuyers();
  }

  trackBuyer(index: number, buyer: PotentialBuyer): string {
    return buyer?.id ?? `${index}`;
  }

  private fetchPotentialBuyers(): void {
    const periodDays = this.filtersForm.get('periodDays')?.value;
    if (periodDays && !this.periodDaysOptions.includes(periodDays)) {
      this.toastr.warning('Período inválido. Selecione 7, 15, 30 ou 60 dias.');
      return;
    }

    const filters: PotentialBuyerFilters = {
      region: this.filtersForm.get('region')?.value,
      modelFilter: this.filtersForm.get('modelFilter')?.value,
      periodDays,
    };

    const request$ = this.buildPotentialBuyersRequest(filters);
    if (!request$) {
      return;
    }

    this.isLoading = true;
    request$
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (response) => {
          this.potentialBuyers = response;
          this.buyers = response?.buyers ?? [];
        }
      });
  }

  private buildPotentialBuyersRequest(filters: PotentialBuyerFilters): Observable<PotentialBuyerResponse> | null {
    if (this.fastQuoteId) {
      return this.fastQuoteService.getPotentialBuyers(this.fastQuoteId, filters);
    }

    if (!this.vehicle?.id) {
      return null;
    }

    return this.oemDealerService.getPotentialBuyers(this.vehicle.id, filters);
  }

  private isDialogData(data: Vehicle | EvaluationVehicle | DialogPotentialDealersData): data is DialogPotentialDealersData {
    return !!data && typeof data === 'object' && 'vehicle' in data;
  }

  getContextTag(contextTag: string): { label: string, value: string, class: string } {
    return this.contextTags.find(tag => tag.value === contextTag) ?? { label: '', value: '', class: '' };
  }

  getAffinityPercent(buyer: PotentialBuyer): number {
    const raw = Number(buyer?.affinityPercent ?? 0);
    if (!Number.isFinite(raw)) {
      return 0;
    }
    return Math.min(Math.max(raw, 0), 100);
  }

  getAffinityRangeClass(buyer: PotentialBuyer): string {
    const t = this.getAffinityPercent(buyer);
    if (t <= 20) {
      return 'is-range-1';
    }
    if (t <= 40) {
      return 'is-range-2';
    }
    if (t <= 60) {
      return 'is-range-3';
    }
    if (t <= 80) {
      return 'is-range-4';
    }
    return 'is-range-5';
  }

  formatPercentage(value: number): string {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  async openWhatsapp(whatsappNumber: string): Promise<void> {
    if (this.vehicle) {
      const message = await this.getWhatsappMessage(this.vehicle as Vehicle);
      whatsappNumber = whatsappNumber.replace(/\D/g, '');
      if (whatsappNumber.length < 12) {
        whatsappNumber = '55' + whatsappNumber;
      }
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    }
  }

  async getWhatsappMessage(vehicle: Vehicle): Promise<string> {
    const url = await this.getVehicleLink(vehicle);
    return `Olá, temos um veículo ${vehicle.make} ${vehicle.trim} ${vehicle.manufactureYear}/${vehicle.modelYear}, 
    com ${vehicle.km} km que está disponível na plataforma Auto Arremate. Gostaria de realizar uma proposta para o veículo? ${url}`;
  }

  async getVehicleLink(vehicle: Vehicle): Promise<string> {
    return this.openService.buildVehicleUrl(vehicle).then(url => url);
  }

}
